import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar"; // Adjust path to your Navbar if needed
import Footer from "../../components/Footer/Footer"; // Adjust path to your Footer if needed
import {
  Check,
  Loader2,
  ShieldCheck,
  Zap,
  Lock,
  CreditCard,
  Sparkles,
  CalendarClock,
  Ban,
  UserCheck,
} from "lucide-react";

export default function SetupBilling({ isDarkMode, toggleTheme }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isHuman, setIsHuman] = useState(false);

  const authToken = localStorage.getItem("token");

  // Safely parse user to get their role and trial history
  let currentUser = {};
  try {
    currentUser = JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    currentUser = {};
  }

  const role = currentUser?.role || "pilot";

  // Whether this user still has a free trial available.
  // Expect the backend to set this on the user object; default to eligible
  // for brand-new accounts.
  const trialEligible = currentUser?.hasUsedTrial ? false : true;

  // ── Plan details + role-specific feature lists ──
  const PLAN_DETAILS = {
    pilot: {
      planName: "Pro Driver Plan",
      planPrice: "$29",
      features: [
        "Unlimited marketplace postings",
        "Real-time direct messaging with companies & dispatchers",
        "Full access to the Premium Load Board",
        "Verified badge on your public profile",
        "Automated, hassle-free monthly billing",
      ],
    },
    company: {
      planName: "Company Carrier Plan",
      planPrice: "$79",
      features: [
        "Post unlimited loads and open routes",
        "Manage your fleet and dispatch team from one dashboard",
        "Direct messaging with verified pilots",
        "Priority placement on the Premium Load Board",
        "Verified Company badge on your public profile",
        "Automated, hassle-free monthly billing",
      ],
    },
    dispatcher: {
      planName: "Dispatcher Plan",
      planPrice: "$59",
      features: [
        "Manage multiple driver profiles in one place",
        "Real-time alerts when new loads match your drivers",
        "Direct messaging with pilots & companies",
        "Full access to the Premium Load Board",
        "Verified Dispatcher badge on your public profile",
        "Automated, hassle-free monthly billing",
      ],
    },
  };

  const { planName, planPrice, features } = PLAN_DETAILS[role] || PLAN_DETAILS.pilot;

  // ── Copy that changes based on trial eligibility ──
  const eyebrowText = trialEligible ? "7-Day Free Trial" : "Premium Access Required";
  const headline = trialEligible
    ? "Start Your 7-Day Free Trial"
    : "Reactivate Your Membership";
  const subheadline = trialEligible
    ? `Try every ${planName} feature free for 7 days. Add a payment method to activate your trial today — you won't be charged until the trial ends, and you can cancel anytime before then.`
    : "Your free trial has ended and your membership is currently inactive. Set up automated billing to restore full access to PilotLinkUSA.";
  const buttonIdleText = trialEligible ? "Start My Free Trial" : "Reactivate My Membership";

  const trialSteps = [
    {
      icon: Sparkles,
      title: "Today",
      description: "Add your card and get instant access to every feature in your plan. $0 charged today.",
    },
    {
      icon: CalendarClock,
      title: "Day 7",
      description: `Your trial ends and billing starts automatically at ${planPrice}/month, unless you've canceled.`,
    },
    {
      icon: Ban,
      title: "Anytime",
      description: "Cancel in one click from your account settings — no fees, no phone calls, no hard feelings.",
    },
  ];

  // Require login to view pricing/setup billing
  useEffect(() => {
    if (!authToken) {
      navigate("/login", { state: { redirectTo: "/setup-billing" } });
    }
    window.scrollTo(0, 0);
  }, [authToken, navigate]);

  const handleSetupBilling = async () => {
    if (!isHuman) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      // Call the backend endpoint to generate the Stripe Checkout Session
      const response = await fetch(`${apiUrl}/api/payments/create-checkout-session/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${authToken}`,
        },
        body: JSON.stringify({ trial: trialEligible }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Safely redirect the user to the secure Stripe hosted checkout page
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to initialize secure checkout. Please try again.");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError("Network error. Please check your connection and try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 flex flex-col">
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8 pt-24 sm:pt-28 pb-16 sm:pb-20">
        <div className="max-w-5xl w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-start">
          {/* ── Left Column: Value Proposition ── */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
              <Zap className="w-3.5 h-3.5" /> {eyebrowText}
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-tight">
              {headline}
            </h1>

            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400">
              {subheadline}
            </p>

            {/* Trial explainer timeline */}
            {trialEligible && (
              <div className="pt-2 pb-2">
                <ol className="space-y-4">
                  {trialSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center shrink-0 shadow-sm">
                        <step.icon className="w-4.5 h-4.5 text-cyan-500" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{step.title}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-snug">
                          {step.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
              <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                What you get with the {planName}:
              </h4>
              <ul className="space-y-4">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-100 dark:bg-cyan-900/40 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700 dark:text-slate-300 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Right Column: Checkout Card ── */}
          <div className="relative md:sticky md:top-28">
            {/* Background glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-[2.5rem] blur opacity-20 dark:opacity-40"></div>

            <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{planName}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {trialEligible ? "7 days free, then billed monthly" : "Billed securely every month"}
                  </p>
                </div>
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center border border-slate-100 dark:border-slate-700 shrink-0">
                  <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-500" />
                </div>
              </div>

              <div className="mb-2 flex items-baseline gap-1 flex-wrap">
                <span className="text-5xl sm:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  {trialEligible ? "$0" : planPrice}
                </span>
                <span className="text-lg font-medium text-slate-500 dark:text-slate-400">
                  {trialEligible ? "today" : "/ month"}
                </span>
              </div>
              {trialEligible && (
                <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
                  then {planPrice}/month after your 7-day trial ends
                </p>
              )}
              {!trialEligible && <div className="mb-8" />}

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl text-sm text-red-600 dark:text-red-400 font-medium flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">⚠️</div>
                  <div>{error}</div>
                </div>
              )}

              {/* Human verification */}
              <label
                htmlFor="isHumanCheck"
                className="mb-4 flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 cursor-pointer select-none hover:border-cyan-300 dark:hover:border-cyan-800 transition-colors"
              >
                <input
                  id="isHumanCheck"
                  type="checkbox"
                  checked={isHuman}
                  onChange={(e) => setIsHuman(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer shrink-0"
                />
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                  <UserCheck className="w-4 h-4 text-cyan-500 shrink-0" />
                  I'm a human
                </span>
              </label>

              <button
                onClick={handleSetupBilling}
                disabled={isLoading || !isHuman}
                className="w-full py-4 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-base sm:text-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-cyan-500"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Connecting to Stripe...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    {buttonIdleText}
                  </>
                )}
              </button>
              {!isHuman && (
                <p className="mt-2 text-xs text-center text-slate-400 dark:text-slate-500">
                  Please confirm you're human to continue
                </p>
              )}

              <div className="mt-6 flex flex-col items-center space-y-4">
                <p className="text-xs text-center text-slate-500 dark:text-slate-400 leading-relaxed max-w-xs mx-auto">
                  {trialEligible
                    ? "No charge today. Payments are processed securely via Stripe once your trial ends, and you can cancel anytime from your account settings."
                    : "Payments are processed securely via Stripe. You can cancel your automated billing at any time from your account settings."}
                </p>

                <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-[10px] font-bold tracking-widest uppercase">Powered by Stripe</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
